/**
 * ASamplesX product page interactions.
 *
 * One behaviour only: the docked purchase bar. It reveals once the hero has
 * scrolled past, and publishes its own height so the rest of the page can
 * reserve room for it.
 *
 * The bar sits directly above the global audio player. That stacking is pure
 * CSS - the bar's `inset-block-end` reads `--asx-player-height`, which
 * `asx-player.js` keeps current - so nothing here needs to know how tall the
 * player is, or whether it is open at all.
 *
 * The header's own 76px -> 64px shrink is NOT here: Horizon's `header.js`
 * already maintains a `data-scroll-direction` attribute, and `asx-chrome-styles`
 * keys the shrink off that, so it costs no JavaScript.
 *
 * Everything commerce-related - add to cart, variants, cart events - stays with
 * Horizon's own components. Nothing in this file touches the cart.
 */

(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;

  function initStickyBar() {
    const bar = document.querySelector('[data-asx-buybar]');
    const hero = document.querySelector('[data-asx-hero]');
    if (!(bar instanceof HTMLElement) || !hero) return;

    if (reduceMotion) bar.setAttribute('data-reduced', '');

    /** Publishes the bar's height, or 0 while it is still tucked away. */
    const measure = () => {
      const height = bar.hasAttribute('data-shown') ? bar.offsetHeight : 0;
      root.style.setProperty('--asx-buybar-height', `${height}px`);
    };

    // An IntersectionObserver on the hero's trailing edge avoids a scroll
    // handler entirely. The root is deliberately the viewport: whether the
    // document scrolls or `.page-wrapper` does, the hero leaves the viewport
    // either way, so this is the one root that reports correctly under both.
    // Rooting it at `.page-wrapper` does not - that element is taller than the
    // viewport, so the hero never stops intersecting it and the bar never shows.
    const observer = new IntersectionObserver(
      ([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.toggleAttribute('data-shown', past);
        measure();
      },
      { threshold: 0 }
    );

    observer.observe(hero);

    // The bar grows a line taller when a long title wraps on a narrow screen.
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(bar);

    measure();
  }

  /**
   * Keeps the docked bar's form on the variant chosen in the purchase panel.
   *
   * The bar carries its own Horizon product form - the same shape Horizon's own
   * quick-add uses - so a submit runs the real Standard Actions path. That form
   * is rendered with the initially selected variant, which for a product with
   * options would go stale the moment the shopper picks a different one. Horizon's
   * variant picker writes the current variant into the hero form's `variantId`
   * input, so mirroring that input is all it takes for the bar to add exactly
   * what the panel above it is showing - never a silent default.
   */
  function initVariantMirror() {
    const hero = document.querySelector('.asx-phero__form [ref="variantId"]');
    const sticky = document.querySelector('.asx-buybar__form [ref="variantId"]');
    if (!(hero instanceof HTMLInputElement) || !(sticky instanceof HTMLInputElement)) return;

    const sync = () => {
      sticky.value = hero.value;
      sticky.disabled = hero.disabled;
      const button = document.querySelector('.asx-buybar__atc');
      if (button instanceof HTMLButtonElement) button.disabled = hero.disabled;
    };

    // The picker replaces the input's value attribute rather than firing an
    // event, so the attribute itself is what gets watched.
    new MutationObserver(sync).observe(hero, {
      attributes: true,
      attributeFilter: ['value', 'disabled'],
    });
    hero.addEventListener('change', sync);
    document.addEventListener('variant:update', sync);

    sync();
  }

  function init() {
    initStickyBar();
    initVariantMirror();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
